import React, {useContext} from 'react';
import {Controller} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {Button} from '@igp/shared/components/button';
import {EditableTextInternal} from '@igp/shared/components/editable';
import {FormControlGroup} from '@igp/shared/components/form';
import {LocalizedText} from '@igp/shared/components/i18n';
import {DateSinglePicker, FormError, RuleType, useValidationFormatter} from '@igp/shared/components/input';
import {ModalContext, ModalFooter} from '@igp/shared/components/modal';
import {useLocale} from '@igp/shared/localization';
import {useReduxForm} from '@igp/shared/redux/form';
import type {Locale} from '@igp/shared/style';
import {getLocalDateWithoutTimezone, secondsSinceEpoch, timestampSecondsToDate} from '@igp/shared/utils';
import moment from 'moment';
import {makeStyles} from 'tss-react/mui';

import {repaymentLogActions} from '../actions';
import type {CreateRepaymentLogPayload, EditRepaymentLogPayload, RepaymentLogViewModel} from '../types';

import {AddRepaymentLogReminderModal} from './ReminderModal';

const localized = defineMessages({
    repaymentDate: {
        id: 'EditRepaymentLogForm_repaymentDate',
        defaultMessage: 'Repayment Date',
    },
    masterAgentId: {
        id: 'EditRepaymentLogForm_masterAgentId',
        defaultMessage: 'Master Agent ID',
    },
    masterPlaceholder: {
        id: 'EditRepaymentLogForm_masterPlaceholder',
        defaultMessage: 'Please input Master Agent ID',
    },
    fromAgentId: {
        id: 'EditRepaymentLogForm_fromAgentId',
        defaultMessage: 'From Agent ID',
    },
    fromPlaceholder: {
        id: 'EditRepaymentLogForm_fromPlaceholder',
        defaultMessage: 'Please input From Agent ID',
    },
    repaymentAmount: {
        id: 'EditRepaymentLogForm_repaymentAmount',
        defaultMessage: 'Repayment Amount',
    },
    submit: {
        id: 'EditRepaymentLogForm_submit',
        defaultMessage: 'Submit',
    },
    cancel: {
        id: 'EditRepaymentLogForm_cancel',
        defaultMessage: 'Cancel',
    },
    reminderTitle: {
        id: 'EditRepaymentLogForm_reminderTitle',
        defaultMessage: 'Repayment Entry Has Been Saved',
    },
});

const useStyles = makeStyles()(theme => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    datePicker: {
        '& .MuiFormControl-root': {
            marginBottom: 0,
        },
    },
}));

type EditRepaymentLogFormProps = {
    log?: RepaymentLogViewModel;
};

export function EditRepaymentLogForm({log}: EditRepaymentLogFormProps) {
    const {classes} = useStyles();
    const {locale} = useLocale();
    const {openModal, closeModal} = useContext(ModalContext);
    const validationFormatter = useValidationFormatter();
    const {formatMessage} = useIntl();
    const {
        control,
        formState: {isDirty},
        state: {isProgress},
        handleSubmit,
        submit,
        cancel,
    } = useReduxForm<RepaymentLogViewModel, EditRepaymentLogPayload | CreateRepaymentLogPayload>({
        initialModel: log ?? {
            repaymentAmount: 0,
            repaymentDate: {
                seconds: null,
            },
            fromAgentUid: '',
            masterAgentUid: '',
        },
        asyncAction: log ? repaymentLogActions.edit : repaymentLogActions.create,
        map: (model: RepaymentLogViewModel) => ({
            repaymentId: model.repaymentId,
            repaymentAmount: Number(model.repaymentAmount),
            repaymentDate: model.repaymentDate,
            fromAgentUid: model.fromAgentUid,
            masterAgentUid: model.masterAgentUid,
        }),
        onSuccess: () =>
            !log
                ? openModal({
                      title: <LocalizedText label={localized.reminderTitle} />,
                      body: <AddRepaymentLogReminderModal />,
                      styleParams: {
                          maxWidth: 'sm',
                      },
                  })
                : closeModal(),
        mode: 'onChange',
        shouldResetOnError: false,
    });

    function handleCancel() {
        cancel();
        closeModal();
    }
    return (
        <>
            <form onSubmit={handleSubmit(submit)} className={classes.form}>
                <FormControlGroup label={formatMessage(localized.repaymentDate)} labelPlacement="left" labelVariant="default">
                    <Controller
                        render={function ({field}) {
                            return (
                                <DateSinglePicker
                                    className={classes.datePicker}
                                    onChange={value => field.onChange(secondsSinceEpoch(getLocalDateWithoutTimezone(moment(value))))}
                                    value={timestampSecondsToDate(field.value)}
                                    locale={locale as Locale}
                                    disableFutureDates
                                />
                            );
                        }}
                        name={'repaymentDate.seconds'}
                        rules={{
                            required: validationFormatter(RuleType.Required, localized.repaymentDate),
                        }}
                        control={control}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.masterAgentId)} labelPlacement="left" labelVariant="default">
                    <Controller
                        render={({field, fieldState}) => {
                            return (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        mode="Edit"
                                        placeholder={formatMessage(localized.masterPlaceholder)}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            );
                        }}
                        name={'masterAgentUid'}
                        control={control}
                        rules={{
                            required: validationFormatter(RuleType.Required, localized.masterAgentId),
                        }}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.fromAgentId)} labelPlacement="left" labelVariant="default">
                    <Controller
                        render={({field, fieldState}) => {
                            return (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        mode="Edit"
                                        placeholder={formatMessage(localized.fromPlaceholder)}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            );
                        }}
                        name={'fromAgentUid'}
                        control={control}
                        rules={{
                            required: validationFormatter(RuleType.Required, localized.fromAgentId),
                        }}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.repaymentAmount)} labelPlacement="left" labelVariant="default">
                    <Controller
                        render={({field, fieldState}) => {
                            return (
                                <>
                                    <EditableTextInternal value={field.value} onChange={field.onChange} mode="Edit" type="number" />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            );
                        }}
                        name={'repaymentAmount'}
                        control={control}
                        rules={{
                            required: validationFormatter(RuleType.Required, localized.repaymentAmount),
                            min: {value: 0, message: validationFormatter(RuleType.Min, localized.repaymentAmount, 0)},
                        }}
                    />
                </FormControlGroup>
            </form>
            <ModalFooter>
                <Button type="button" onClick={handleCancel} label={localized.cancel} disabled={isProgress} />
                <Button
                    type="button"
                    onClick={handleSubmit(submit)}
                    color="primary"
                    label={localized.submit}
                    disabled={!isDirty || isProgress}
                />
            </ModalFooter>
        </>
    );
}
